import { FormActions } from '@openx/components-core/lib/FormActions/FormActions';
import type { PackageFormState } from '@openx/types/package';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import isEmpty from 'lodash/isEmpty';
import { type JSX, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePackageContext } from '../../../utils/context/PackageContext';
import { usePackageEditPermissions } from '../../../utils/hooks/usePackageEditPermissions';
import { useTargetingBusinessRequirements } from '../hooks/useTargetingBusinessRequirements';
import { getDiff } from '../utils/formStateConverter';
import { DetailsSection } from './DetailsSection';
import { DirectRatesSection } from './DirectRatesSection';
import { MerchandisingSection } from './MerchandisingSection';
import { OnBehalfOfForm } from './OnBehalfOfForm';
import { PrivateMarketSection } from './PrivateMarketSection';
import { TargetingSection } from './TargetingSection';

type PackageFormSectionsProps = {
  goBack?: () => void;
  currency: string;
  setSelectedAccountId?: (id: string) => void;
  isEdit?: boolean;
  isSubmitting: boolean;
  initialValues: PackageFormState;
  resetFormData?: PackageFormState;
  openXAgentTools?: (isSubmitting: boolean, formValues: PackageFormState) => JSX.Element;
};

export const PackageFormSections = ({
  initialValues,
  isSubmitting,
  goBack,
  currency,
  isEdit = false,
  setSelectedAccountId,
  resetFormData,
  openXAgentTools,
}: PackageFormSectionsProps): JSX.Element => {
  const { t } = useTranslation();

  const { watch, reset } = useFormContext<PackageFormState>();
  const { isOa } = usePermissionContext();
  const { usePackagePermissions } = usePackageContext();
  const { isInternalUser, shouldSeeOnBehalfOf } = usePackagePermissions();
  const { isLockedForNonCurator, isLockedForExternalUsers } = usePackageEditPermissions(initialValues);

  useEffect(() => {
    if (resetFormData) {
      reset(resetFormData);
    }
  }, [resetFormData, reset]);

  const isMerchandisingAndPrivateMarkedLocked = !!(isOa && !isInternalUser);
  const isEditAndLockedForExternalUsers = isEdit && isLockedForExternalUsers;

  const observableWatchedFormValues = watch();

  const { isTargetingBlockingRulesSchemaLoading, isTargetingBlockingRulesValidationPending } =
    useTargetingBusinessRequirements();

  const isFormUnchanged = useMemo(
    () => isEmpty(getDiff(observableWatchedFormValues, initialValues)),
    [initialValues, observableWatchedFormValues]
  );

  const isEditAndFormUnchanged = isEdit && isFormUnchanged;

  const areFormActionsDisabled =
    isSubmitting ||
    isEditAndFormUnchanged ||
    isEditAndLockedForExternalUsers ||
    isLockedForNonCurator ||
    isTargetingBlockingRulesSchemaLoading ||
    isTargetingBlockingRulesValidationPending;

  return (
    <>
      {shouldSeeOnBehalfOf && <OnBehalfOfForm isEdit={isEdit} setSelectedAccountId={setSelectedAccountId} />}
      <DetailsSection />
      {!isMerchandisingAndPrivateMarkedLocked && <MerchandisingSection />}
      <TargetingSection initialValues={initialValues} />
      <DirectRatesSection currency={currency} />
      {!isMerchandisingAndPrivateMarkedLocked && <PrivateMarketSection currency={currency} />}

      <FormActions
        submitButtonLabel={t('Save')}
        cancelButtonLabel={t('Cancel')}
        onCancel={goBack || false}
        disabled={areFormActionsDisabled}
        isSubmitting={isSubmitting}
        openXAgentTools={
          openXAgentTools !== undefined ? () => openXAgentTools(isSubmitting, observableWatchedFormValues) : undefined
        }
      />
    </>
  );
};
